// ─────────────────────────────────────────────────────────────────────────────
// EVERY FILE ACROSS THE FIRM — PROGRESS, NEVER CONTENTS.
//
// 🚨 WHAT WIDENS HERE IS *WHOSE* FILES, NOT *WHAT* IS SHOWN OF THEM. The rule
// that governs an agent's own My Buyers governs this page unchanged: status and
// next step, never documents, income, credit, dollar amounts or internal stage.
// A broker is senior to their agents and no more entitled to a borrower's file.
//
// ⚠️ AND THERE IS NO SHOPPING RANGE ON THIS SCREEN AT ALL. My Buyers shows a
// ceiling when the borrower consented to share it — and that consent is recorded
// per AGENT (partner_range_consent is keyed borrower + partner_user). It was
// never given to the brokerage. The backend does not select the figure, so there
// is nothing here to render even by accident; see utils/firmRollup.js.
//
// ⚠️ SAME SHELL, SAME CARDS, SAME TYPOGRAPHY AS THE REST OF DoorlyPro. This is a
// capability on the existing portal, not a second product — and two portals meant
// to look identical are a mirror-drift hazard in CSS. StatusBadge and the table
// chrome match DoorlyProReferrals deliberately.
import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import DoorlyProLayout, { RequirePartnerAuth } from '../../components/DoorlyProLayout';
import { useDoorlyProAuth } from '../../context/DoorlyProAuthContext';
import { getStatusMeta } from '../../constants/partnerStatuses';
import { firmPipelineCopy } from '../../constants/firmPipelineCopy';
import WithdrawFileControl from '../../components/doorlypro/WithdrawFileControl';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';

function StatusBadge({ status, partnerType }) {
  const meta = getStatusMeta(status, partnerType);
  const s = meta.color;
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: 5,
      padding: '3px 10px', borderRadius: 20, fontSize: 12, fontWeight: 600,
      backgroundColor: s.bg, color: s.color, whiteSpace: 'nowrap',
    }}>
      <span style={{ width: 6, height: 6, borderRadius: '50%', backgroundColor: s.color }} />
      {meta.label}
    </span>
  );
}

function DoorlyProFirmPipeline() {
  const { token } = useDoorlyProAuth();
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [agentFilter, setAgentFilter] = useState('');
  // 🔑 THE FIRM'S TYPE, FROM THE SERVER (Hannah approved, 2026-11-02): an LO firm reads "borrowers" and "team members".
  const [firmPartnerType, setFirmPartnerType] = useState(null);
  const copy = firmPipelineCopy(firmPartnerType);
  const isLoFirm = firmPartnerType === 'loan_officer';
  // 🔑 broker gaps (2026-11-02): the server's switch for "Withdraw file" (on by default since Hannah approved the wording 2026-11-03; "off" kills it).
  const [withdrawEnabled, setWithdrawEnabled] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    if (!token) return;
    axios.get(`${API_URL}/api/doorlypro/firm/pipeline`, { headers: { Authorization: `Bearer ${token}` } })
      .then((res) => {
        setFiles(res.data?.files || []);
        setFirmPartnerType(res.data?.firm_partner_type || null);
        setWithdrawEnabled(!!res.data?.withdraw_enabled);
      })
      // ⚠️ A 404 HERE IS THE ROLE GUARD, NOT A MISSING PAGE. requireBroker 404s
      // rather than 403s so that "you may not see this" does not confirm the firm
      // view exists — so the copy must not say "not found" either.
      .catch(() => setError('This view isn’t available on your account.'))
      .finally(() => setLoading(false));
  }, [token, reloadKey]);

  const agents = useMemo(() => {
    const seen = new Map();
    files.forEach((f) => {
      const name = `${f.agent_first_name || ''} ${f.agent_last_name || ''}`.trim() || 'Unassigned';
      seen.set(f.partner_id, name);
    });
    return [...seen.entries()].sort((a, b) => a[1].localeCompare(b[1]));
  }, [files]);

  const shown = agentFilter ? files.filter((f) => String(f.partner_id) === agentFilter) : files;

  return (
    <RequirePartnerAuth>
      <DoorlyProLayout>
        <div style={{ marginBottom: 24 }}>
          <h1 style={{ fontSize: 22, fontWeight: 800, color: '#111827', margin: '0 0 4px' }}>Team Pipeline</h1>
          <p style={{ fontSize: 14, color: '#6b7280', margin: '0 0 14px' }}>
            {copy.lead}
          </p>

          {/* 🚨 THE BOUNDARY IS STATED ON THE SCREEN, NOT ONLY ENFORCED IN SQL. A
              broker who does not know what they are not being shown assumes the
              page is incomplete and asks staff for the rest. Saying it here ends
              that conversation before it starts — and it is the same sentence
              their agents are told about their own view. */}
          <div style={{
            display: 'flex', gap: 8, alignItems: 'flex-start',
            padding: '10px 14px', background: '#f9fafb', border: '1px solid #e5e7eb',
            borderRadius: 10, fontSize: 13, color: '#4b5563', maxWidth: 680,
          }}>
            <span aria-hidden="true">🔒</span>
            {isLoFirm ? (
              <span>
                You see each file’s <strong>progress</strong>, the same as your team members do — never a
                borrower’s documents, income, credit or any dollar figure. Approval amounts are shared
                only when a borrower chooses to share them, with the team member they chose.
              </span>
            ) : (
              <span>
                You see each file’s <strong>progress</strong>, the same as your agents do — never a
                buyer’s documents, income, credit or any dollar figure. Approval amounts are shared
                only when a buyer chooses to share them, with the agent they chose.
              </span>
            )}
          </div>
        </div>

        <div style={{ backgroundColor: '#fff', borderRadius: 12, border: '1px solid #e5e7eb', overflow: 'hidden' }}>
          {error && (
            <div style={{ padding: '12px 20px', backgroundColor: '#fff0f3', color: '#ff082f', fontSize: 13 }}>{error}</div>
          )}

          {!error && agents.length > 1 && (
            <div style={{ padding: '12px 16px', borderBottom: '1px solid #f3f4f6', display: 'flex', alignItems: 'center', gap: 10 }}>
              <label htmlFor="agent" style={{ fontSize: 12, fontWeight: 600, color: '#6b7280' }}>{copy.filterLabel}</label>
              <select
                id="agent" value={agentFilter} onChange={(e) => setAgentFilter(e.target.value)}
                style={{ padding: '6px 10px', borderRadius: 8, border: '1px solid #e5e7eb', fontSize: 13, background: '#fff' }}
              >
                <option value="">Everyone ({files.length})</option>
                {agents.map(([id, name]) => (
                  <option key={id} value={String(id)}>{name}</option>
                ))}
              </select>
            </div>
          )}

          {loading ? (
            <div style={{ padding: 40, textAlign: 'center', color: '#9ca3af', fontSize: 14 }}>{copy.loading}</div>
          ) : error ? null : shown.length === 0 ? (
            <div style={{ padding: 40, textAlign: 'center' }}>
              <p style={{ color: '#6b7280', fontSize: 14, margin: 0 }}>
                {files.length === 0 ? copy.emptyFirm : copy.emptyFiltered}
              </p>
            </div>
          ) : (
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                <thead>
                  <tr style={{ backgroundColor: '#f9fafb', borderBottom: '1px solid #e5e7eb' }}>
                    {copy.columns.map((col) => (
                      <th key={col} style={{ padding: '10px 16px', textAlign: 'left', fontWeight: 600, color: '#6b7280', whiteSpace: 'nowrap', fontSize: 12 }}>
                        {col}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {shown.map((f, idx) => {
                    // 🔑 A broker submission (source 'submission', utils/firmRollup) is an LO's loan file: its status reads
                    // with the loan-officer copy, as on My Borrowers. Its id is a submission id, so the key carries the source.
                    const pType = f.source === 'submission' ? 'loan_officer' : undefined;
                    const meta = getStatusMeta(f.partner_status, pType);
                    return (
                      <tr key={`${f.source || 'referral'}-${f.id}`} data-testid="firm-pipeline-row" data-source={f.source || 'referral'} style={{ borderBottom: idx < shown.length - 1 ? '1px solid #f3f4f6' : 'none' }}>
                        <td style={{ padding: '12px 16px', color: '#111827', fontWeight: 600 }}>
                          {`${f.first_name || ''} ${f.last_name || ''}`.trim() || f.email}
                          {/* 🔑 A member's loan file the borrower has not registered on can be withdrawn by the broker of
                              record (broker gaps, 2026-11-02) — only when the server's switch is on. */}
                          {withdrawEnabled && f.source === 'submission' && f.partner_status === 'Invite not yet accepted' && (
                            <div style={{ marginTop: 4, fontWeight: 400 }}>
                              <WithdrawFileControl token={token} submissionId={f.id}
                                borrowerName={`${f.first_name || ''} ${f.last_name || ''}`.trim() || f.email}
                                onWithdrawn={() => setReloadKey((k) => k + 1)} />
                            </div>
                          )}
                        </td>
                        <td style={{ padding: '12px 16px', color: '#4b5563', whiteSpace: 'nowrap' }}>
                          {`${f.agent_first_name || ''} ${f.agent_last_name || ''}`.trim() || '—'}
                          {/* 🔑 A REMOVED MEMBER'S FILES STAY ON THE FIRM'S PIPELINE (broker gaps, 2026-11-02), marked with the
                              label Your Team already uses for an archived member ('Removed'). No new wording. */}
                          {f.agent_removed && (
                            <span data-testid="firm-pipeline-agent-removed" style={{ marginLeft: 6, color: '#9ca3af', fontSize: 12 }}>
                              · Removed
                            </span>
                          )}
                        </td>
                        <td style={{ padding: '12px 16px' }}><StatusBadge status={f.partner_status} partnerType={pType} /></td>
                        {/* ⚠️ THE SAME nextStep COPY THE AGENT READS, from the same
                            source, so the firm and the agent describe a file in
                            identical words — and Eva voices those same words. */}
                        <td style={{ padding: '12px 16px', color: '#6b7280', maxWidth: 380 }}>
                          {meta.nextStep || '—'}
                        </td>
                        <td style={{ padding: '12px 16px', color: '#9ca3af', whiteSpace: 'nowrap' }}>
                          {f.created_at ? new Date(f.created_at).toLocaleDateString() : '—'}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </DoorlyProLayout>
    </RequirePartnerAuth>
  );
}

export default DoorlyProFirmPipeline;
